import React, { useState, useEffect } from 'react';
import { CallBtn, WABtn, PhoneField } from '../components/ui/PhoneButtons';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Plus, X, Search, Building2, Edit2, Trash2, Zap, Droplets } from 'lucide-react';
import SearchableSelect from '../components/ui/SearchableSelect';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// A hostel record whose student already has generated bills must never be deleted - vacate it instead.
// Returns '' when deleting is allowed, otherwise the reason.
async function hostelBillsBlockDelete(alloc) {
  const sid = alloc && alloc.student_id;
  if (!sid) return '';
  const q = t => supabase.from(t).select('student_id', { count: 'exact', head: true }).eq('student_id', sid);
  const [sp, ub, mc] = await Promise.all([q('hostel_bill_splits'), q('hostel_utility_bills'), q('hostel_monthly_charges')]);
  if (sp.error || ub.error || mc.error) return 'Could not check this student’s hostel bills just now, so the record was not deleted. Please try again.';
  const n = (sp.count || 0) + (ub.count || 0) + (mc.count || 0);
  if (n > 0) return 'This student already has generated hostel bills (' + n + ' entr' + (n === 1 ? 'y' : 'ies') + '). Deleting the record would lose that history, so it is blocked. Use Vacate instead - the bills stay exactly as they are.';
  return '';
}

function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  const sorted = [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
  try { window.__batchRank = new Map(sorted.map((b, i) => [b.batch_name, i])); } catch (e) {}
  return sorted;
}
// Order plain batch names the same way as the batch dropdowns (ongoing first, newest first); unknown names go last.
function orderBatchNames(names) {
  const r = window.__batchRank;
  return [...names].sort((a, b) => {
    const pa = r && r.has(a) ? r.get(a) : 1e9, pb = r && r.has(b) ? r.get(b) : 1e9;
    if (pa !== pb) return pa - pb;
    return String(b).localeCompare(String(a), undefined, { numeric: true });
  });
}

// Sort students by register number the natural way (TM23002 before TM23010).
const byRegNo = (a, b) => String(a.register_number || '').localeCompare(String(b.register_number || ''), undefined, { numeric: true });

const OWNER_MAIL = 'ershadarmstech@gmail.com';
const OFFICE_MAILS = ['armstechac@gmail.com', 'armstechekm@gmail.com'];

export default function Hostel() {
  const { isEditor, canEdit, canEditBatch, assignedBatches, isCoordinator, isAdmin, staff, user } = useAuth();
  // staff assigned to specific batches can change only those batches' students; everything else is view-only
  const meMail = String(user?.email || '').trim().toLowerCase();
  const batchRestricted = meMail !== OWNER_MAIL && !OFFICE_MAILS.includes(meMail) && !isAdmin && ((assignedBatches || []).length > 0 || !!isCoordinator);
  const [allocations, setAllocations] = useState([]);
  const [vacatedAllocations, setVacatedAllocations] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBillModal, setShowBillModal] = useState(null);
  const [showBulkBillModal, setShowBulkBillModal] = useState(false);
  const [bulkBillForm, setBulkBillForm] = useState({ hostel_id:'', billing_month: new Date().getMonth()+1, billing_year: new Date().getFullYear(), electricity_amount:'', water_amount:'', other_amount:'' });
  const [bulkBillSaving, setBulkBillSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterBatch, setFilterBatch]     = useState('');
  const [filterHostel, setFilterHostel]   = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterRoom, setFilterRoom]       = useState('');
  const [filterKeys, setFilterKeys]       = useState('');
  const [filterKeysRet, setFilterKeysRet] = useState('');
  const [batches, setBatches] = useState([]);
  const ownBatch = name => !batchRestricted || (typeof canEditBatch === 'function' && batches.some(b => b.batch_name === name && canEditBatch(b.id)));
  const canEditH = canEdit('hostel') || batchRestricted;   // an assigned batch grants edit on that batch even if the section is View
  const canEditRow = name => canEditH && ownBatch(name);
  const NOT_OWN = 'You can change only your own batches';
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('allocations');
  const [monthlyCharges, setMonthlyCharges] = useState([]);
  const [vacatedHostelFilter, setVacatedHostelFilter] = useState('');
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [mFilter, setMFilter] = useState({hostel:'',month:'',year:new Date().getFullYear()});
  const [showAddCharge, setShowAddCharge] = useState(false);
  const [chargeForm, setChargeForm] = useState({hostel_id:'',month:new Date().getMonth()+1,year:new Date().getFullYear(),amount:'',remarks:''});
  const [chargeSaving, setChargeSaving] = useState(false);
  const [chargeStudents, setChargeStudents] = useState([]);
  const [chargeStudentsLoading, setChargeStudentsLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [vacateConfirm, setVacateConfirm] = useState(null);

  const emptyForm = { student_id: '', hostel_id: '', room_number: '', joined_date: '', keys_issued: false, hostel_status: 'Active', keys_returned: false, hostel_vacate_date: '', rules_form_submitted: '', remarks: '', rent_payments: [], _modalBatch: '' };
  const [form, setForm] = useState(emptyForm);
  const [billForm, setBillForm] = useState({ billing_month: new Date().getMonth() + 1, billing_year: new Date().getFullYear(), electricity_amount: '', water_amount: '', other_amount: '', payment_status: 'unpaid' });
  const [bills, setBills] = useState([]);

  useEffect(() => { loadAll(); }, []);


  async function loadAll() {
    const [a, av, h, s, b] = await Promise.all([
      supabase.from('hostel_allocations').select('*, students(full_name, register_number, batch_name), hostels(hostel_name), rent_payments').is('vacate_date', null).order('joined_date', { ascending: false }).limit(500),
      supabase.from('hostel_allocations').select('*, students(full_name, register_number, batch_name), hostels(hostel_name)').or('vacate_date.not.is.null,hostel_status.eq.Vacated').order('vacate_date', { ascending: false }).limit(200),
      supabase.from('hostels').select('*'),
      supabase.from('students').select('id, full_name, register_number, batch_id, batch_name, contact_number, whatsapp_number, guardian_name').eq('is_active', true).limit(3000),
      supabase.from('batches').select('id, batch_name, status, start_date').order('batch_name'),
    ]);
    setAllocations(a.data || []);
    setVacatedAllocations(av.data || []);
    setHostels(h.data || []);
    setStudents(s.data || []);
    setBatches(sortBatches(b.data || []));
    setLoading(false);
  }

  async function save() {
    if (!form.student_id || !form.hostel_id) { toast.error('Fill required fields'); return; }
    { const stu = students.find(x => x.id === form.student_id); if (!canEditRow(stu?.batch_name || stu?.batches?.batch_name)) { toast.error(NOT_OWN); return; } }
    // a student can have only ONE active hostel record - a second one (same or another hostel) is refused
    if (!form.hostel_vacate_date && (form.hostel_status || 'Active') !== 'Vacated') {
      let dq = supabase.from('hostel_allocations').select('id,joined_date,hostels(hostel_name)')
        .eq('student_id', form.student_id).is('vacate_date', null).or('hostel_status.is.null,hostel_status.neq.Vacated');
      if (editId) dq = dq.neq('id', editId);
      const { data: dup, error: dupErr } = await dq;
      if (dupErr) { toast.error('Could not check for an existing hostel record: ' + dupErr.message); return; }
      if (dup && dup.length) {
        const who = (students.find(x => x.id === form.student_id) || {}).full_name || 'This student';
        const d = dup[0];
        toast.error('Duplicate entry: ' + who + ' already has an active hostel record in ' + ((d.hostels && d.hostels.hostel_name) || 'a hostel') + (d.joined_date ? ' (joined ' + fmtDMY(d.joined_date) + ')' : '') + '. A student cannot be added to a second hostel. Vacate or edit the existing record first.', { duration: 10000 });
        return;
      }
    }
    setSaving(true);
    const { _modalBatch, rent_payments, ...rest } = form;
    const cleanRentPayments = (rent_payments || []).map(p => ({ month: p.month, amount: p.amount }));
    const netRent = cleanRentPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);

    const payload = {
      ...rest,
      keys_returned:      form.keys_returned || false,
      hostel_vacate_date: form.hostel_vacate_date || null,
      vacate_date:        form.hostel_vacate_date || null,
      hostel_status:      form.hostel_vacate_date ? 'Vacated' : (form.hostel_status || 'Active'),
      rent_payments:      cleanRentPayments,
    };

    let error;
    if (editId) {
      ({ error } = await supabase.from('hostel_allocations').update(payload).eq('id', editId));
    } else {
      ({ error } = await supabase.from('hostel_allocations').insert(payload));
    }
    if (error) { toast.error(error.message); setSaving(false); return; }

    // Always update hostel_fee AND recalculate fee_total in students table
    // First get current student fee fields
    const { data: studentData } = await supabase
      .from('students')
      .select('fee_tuition, fee_attestation, fee_package_change, fee_manpower, fee_certification, fee_other, fee_discount')
      .eq('id', form.student_id)
      .single();

    if (studentData) {
      const newFeeTotal =
        Number(studentData.fee_tuition || 0) +
        Number(netRent) +
        Number(studentData.fee_attestation || 0) +
        Number(studentData.fee_package_change || 0) +
        Number(studentData.fee_manpower || 0) +
        Number(studentData.fee_certification || 0) +
        Number(studentData.fee_other || 0) -
        Number(studentData.fee_discount || 0);

      const { error: feeError } = await supabase
        .from('students')
        .update({ hostel_fee: netRent, fee_total: newFeeTotal })
        .eq('id', form.student_id);

      if (feeError) toast.error('Saved but fee update failed: ' + feeError.message);
      else toast.success(`Hostel ${editId ? 'updated' : 'allocated'}${netRent > 0 ? ` & ₹${netRent.toLocaleString('en-IN')} added to fees` : ''} ✅`);
    } else {
      toast.success(`Hostel ${editId ? 'updated' : 'allocated'} ✅`);
    }

    setShowModal(false); setForm(emptyForm); setEditId(null); loadAll(); setSaving(false);
  }

  function openEdit(a) {
    if (!canEditRow(a.students?.batch_name)) { toast.error(NOT_OWN); return; }
    setEditId(a.id);
    const student = students.find(s => s.id === a.student_id);
    const studentBatchName = student?.batch_name || student?.batches?.batch_name || a.students?.batch_name || a.students?.batches?.batch_name || '';
    setForm({
      student_id:           a.student_id || '',
      hostel_id:            a.hostel_id || '',
      room_number:          a.room_number || '',
      joined_date:          a.joined_date || '',
      keys_issued:          a.keys_issued || false,
      hostel_status:        a.hostel_status || 'Active',
      keys_returned:        a.keys_returned || false,
      hostel_vacate_date:   a.hostel_vacate_date || '',
      rules_form_submitted: a.rules_form_submitted || '',
      remarks:              a.remarks || '',
      rent_payments:        a.rent_payments || [],
      _modalBatch:          studentBatchName,
    });
    setShowModal(true);
  }

  async function confirmVacate() {
    if (!vacateConfirm) return;
    if (!canEditRow(vacateConfirm.students?.batch_name)) { toast.error(NOT_OWN); return; }
    const { error } = await supabase.from('hostel_allocations')
      .update({ vacate_date: new Date().toISOString().split('T')[0], keys_handed_over: true, hostel_status: 'Vacated' })
      .eq('id', vacateConfirm.id);
    if (error) toast.error(error.message);
    else { toast.success('Student vacated successfully'); setVacateConfirm(null); setActiveTab('vacated'); loadAll(); }
  }

  async function openBills(allocation) {
    setShowBillModal(allocation);
    const { data } = await supabase.from('hostel_utility_bills').select('*').eq('student_id', allocation.student_id).order('billing_year', { ascending: false }).order('billing_month', { ascending: false });
    setBills(data || []);
  }

  async function saveBill() {
    if (!canEditRow(showBillModal?.students?.batch_name)) { toast.error(NOT_OWN); return; }
    if (!billForm.billing_month || !billForm.billing_year) { toast.error('Fill billing period'); return; }
    const { error } = await supabase.from('hostel_utility_bills').upsert({
      student_id: showBillModal.student_id, hostel_allocation_id: showBillModal.id,
      billing_month: Number(billForm.billing_month), billing_year: Number(billForm.billing_year),
      electricity_amount: Number(billForm.electricity_amount || 0),
      water_amount: Number(billForm.water_amount || 0),
      other_amount: Number(billForm.other_amount || 0),
      payment_status: billForm.payment_status
    }, { onConflict: 'student_id,billing_month,billing_year' });
    if (error) toast.error(error.message);
    else { toast.success('Bill saved'); openBills(showBillModal); }
  }

  async function saveBulkBills() {
    if (batchRestricted) { toast.error('Bulk bills for a whole hostel can be added by office admin only'); return; }
    if (!bulkBillForm.hostel_id) { toast.error('Select a hostel'); return; }
    if (!bulkBillForm.billing_month || !bulkBillForm.billing_year) { toast.error('Select billing period'); return; }
    setBulkBillSaving(true);
    // Get all active allocations for selected hostel
    const hostelAllocs = allocations.filter(a => a.hostel_id === bulkBillForm.hostel_id);
    if (!hostelAllocs.length) { toast.error('No students in this hostel'); setBulkBillSaving(false); return; }
    const rows = hostelAllocs.map(a => ({
      student_id: a.student_id,
      hostel_allocation_id: a.id,
      billing_month: Number(bulkBillForm.billing_month),
      billing_year: Number(bulkBillForm.billing_year),
      electricity_amount: Number(bulkBillForm.electricity_amount||0),
      water_amount: Number(bulkBillForm.water_amount||0),
      other_amount: Number(bulkBillForm.other_amount||0),
      payment_status: 'unpaid',
    }));
    const { error } = await supabase.from('hostel_utility_bills').upsert(rows, { onConflict:'student_id,billing_month,billing_year' });
    if (error) toast.error(error.message);
    else {
      toast.success(`✅ Bills added for ${rows.length} students`);
      setShowBulkBillModal(false);
      setBulkBillForm({ hostel_id:'', billing_month: new Date().getMonth()+1, billing_year: new Date().getFullYear(), electricity_amount:'', water_amount:'', other_amount:'' });
    }
    setBulkBillSaving(false);
  }

  async function deleteBill(billId) {
    if (!canEditRow(showBillModal?.students?.batch_name)) { toast.error(NOT_OWN); return; }
    if (!window.confirm('Delete this utility bill?')) return;
    const { error } = await supabase.from('hostel_utility_bills').delete().eq('id', billId);
    if (error) toast.error(error.message);
    else { toast.success('Bill deleted'); openBills(showBillModal); }
  }

  const hostelStats = hostels.map(h => ({
    ...h,
    occupied: allocations.filter(a => a.hostel_id === h.id).length
  }));

  const filtered = allocations.filter(a => {
    const q = search.toLowerCase();
    const matchSearch  = !q || a.students?.full_name?.toLowerCase().includes(q) || a.students?.register_number?.toLowerCase().includes(q);
    const matchBatch   = !filterBatch    || (a.students?.batches?.batch_name || a.students?.batch_name) === filterBatch;
    const matchHostel  = !filterHostel   || a.hostel_id === filterHostel;
    const matchStatus  = !filterStatus   || (a.hostel_status || 'Active') === filterStatus;
    const matchRoom    = !filterRoom     || a.room_number === filterRoom;
    const matchKeys    = !filterKeys     || (filterKeys === 'yes' ? a.keys_issued : !a.keys_issued);
    const matchKeysRet = !filterKeysRet  || (filterKeysRet === 'yes' ? a.keys_returned : !a.keys_returned);
    return matchSearch && matchBatch && matchHostel && matchStatus && matchRoom && matchKeys && matchKeysRet;
  });

  // Get unique room numbers for filter
  const roomNumbers = [...new Set(allocations.map(a => a.room_number).filter(Boolean))].sort();

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  async function loadMonthlyCharges(hostelId, month, year) {
    setMonthlyLoading(true);
    let q = supabase.from('hostel_monthly_charges')
      .select('*,students(full_name,register_number,batch_name)')
      .order('year',{ascending:false}).order('month',{ascending:false});
    if (hostelId) q = q.eq('hostel_id', hostelId);
    if (month) q = q.eq('month', month);
    if (year) q = q.eq('year', year);
    const {data} = await q;
    setMonthlyCharges(data||[]);
    setMonthlyLoading(false);
  }

  return (
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">Hostel Management</h1><p className="page-subtitle">Manage 3 hostels, room allocation, and utility billing</p></div>
        {canEditH && <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={14} /> Allocate Hostel</button>}
      </div>

      {/* Hostel overview */}
      <div style={{ display: 'grid', gridTemplateColumns: (window.innerWidth<=768)?'1fr 1fr':'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        {hostelStats.map(h => {
          const isActive = filterHostel === h.id;
          return (
            <div
              className="card"
              key={h.id}
              onClick={() => {
                setFilterHostel(isActive ? '' : h.id);
                setActiveTab('allocations');
              }}
              style={{
                padding: '16px 20px',
                cursor: 'pointer',
                border: isActive ? '2px solid var(--cyan)' : '1px solid var(--border)',
                background: isActive ? 'rgba(6,182,212,0.06)' : undefined,
                transform: isActive ? 'translateY(-2px)' : undefined,
                boxShadow: isActive ? '0 4px 16px rgba(6,182,212,0.2)' : undefined,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Building2 size={16} color={isActive ? 'var(--cyan)' : 'var(--text3)'} />
                <span style={{ fontWeight: 600, fontSize: 14, color: isActive ? 'var(--cyan)' : 'var(--text)' }}>{h.hostel_name}</span>
                {isActive && <span style={{ marginLeft: 'auto', fontSize: 10, background: 'var(--cyan)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontWeight: 700 }}>ACTIVE</span>}
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--cyan)', fontFamily: 'var(--font-display)' }}>{h.occupied}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>/ {(h.total_rooms || 0) * (h.capacity_per_room || 1)} capacity</div>
              <div className="progress-bar" style={{ marginTop: 8 }}>
                <div className="progress-fill" style={{ width: `${Math.min(100, (h.occupied / ((h.total_rooms || 1) * (h.capacity_per_room || 1))) * 100)}%`, background: 'var(--cyan)' }} />
              </div>
              <div style={{ fontSize: 11, color: isActive ? 'var(--cyan)' : 'var(--text3)', marginTop: 6, fontWeight: isActive ? 600 : 400 }}>
                {isActive ? '✕ Click to clear filter' : 'Click to view students'}
              </div>
            </div>
          );
        })}
      </div>

      <div className="tabs">
        <div className={`tab ${activeTab === 'allocations' ? 'active' : ''}`} onClick={() => setActiveTab('allocations')}>Current Allocations</div>
        <div className={`tab ${activeTab === 'vacated' ? 'active' : ''}`} onClick={() => setActiveTab('vacated')}>Vacated</div>
        <div className={`tab ${activeTab === 'bills' ? 'active' : ''}`} onClick={() => setActiveTab('bills')}>⚡💧 KSEB & KWA Bills</div>
        <div className={`tab ${activeTab === 'monthly' ? 'active' : ''}`} onClick={() => { setActiveTab('monthly'); loadMonthlyCharges('','',new Date().getFullYear()); }}>🏠 Monthly Charges</div>
      </div>

      <div style={{ marginBottom: 16 }}>
        {activeTab === 'allocations' && (<>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
          <div className="search-bar">
            <Search /><input placeholder="Search name or register no..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <SearchableSelect
            options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))}
            value={filterBatch}
            onChange={v => setFilterBatch(v)}
            placeholder="All Batches"
            searchPlaceholder="Search batch..."
            style={{ minWidth: 140, maxWidth: 160 }}
          />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 120 }}>
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Vacated">Vacated</option>
          </select>
          <select value={filterRoom} onChange={e => setFilterRoom(e.target.value)} style={{ width: 120 }}>
            <option value="">All Rooms</option>
            {roomNumbers.map(r => <option key={r} value={r}>Room {r}</option>)}
          </select>
          <select value={filterKeys} onChange={e => setFilterKeys(e.target.value)} style={{ width: 130 }}>
            <option value="">Keys Collected</option>
            <option value="yes">✅ Collected</option>
            <option value="no">❌ Not Collected</option>
          </select>
          <select value={filterKeysRet} onChange={e => setFilterKeysRet(e.target.value)} style={{ width: 130 }}>
            <option value="">Keys Returned</option>
            <option value="yes">✅ Returned</option>
            <option value="no">❌ Not Returned</option>
          </select>
          {filterHostel && (
            <span style={{ fontSize: 12, background: 'rgba(6,182,212,0.1)', color: 'var(--cyan)', border: '1px solid var(--cyan)', borderRadius: 20, padding: '3px 10px', fontWeight: 600 }}>
              🏠 {hostels.find(h => h.id === filterHostel)?.hostel_name}
            </span>
          )}
          {(search || filterBatch || filterHostel || filterStatus || filterRoom || filterKeys || filterKeysRet) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilterBatch(''); setFilterHostel(''); setFilterStatus(''); setFilterRoom(''); setFilterKeys(''); setFilterKeysRet(''); }}>✕ Clear All</button>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>{filtered.length} student{filtered.length !== 1 ? 's' : ''} shown</div>
        </>)}
      </div>

      {/* Current Allocations Tab */}
      {canEditH && !batchRestricted && activeTab === 'allocations' && (
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
          <button className="btn btn-secondary btn-sm" onClick={()=>setShowBulkBillModal(true)}>⚡ Bulk Add Utility Bills</button>
        </div>
      )}
      {activeTab === 'allocations' && (
        loading ? <div className="loading"><div className="spinner" /></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Student</th><th>Batch</th><th>Hostel</th><th>Room</th><th>Joined Date</th><th>Keys Issued</th><th>Rules Form</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8}><div className="empty-state"><Building2 /><h3>No hostel allocations</h3></div></td></tr>
                ) : filtered.map(a => (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{a.students?.full_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>{a.students?.register_number}</div>
                    </td>
                    <td><span className="badge badge-blue" style={{fontSize:11}}>{a.students?.batches?.batch_name || a.students?.batch_name || '—'}</span></td>
                    <td><span className="badge badge-cyan">{a.hostels?.hostel_name}</span></td>
                    <td>{a.room_number || '—'}</td>
                    <td style={{ fontSize: 12 }}>{fmtDMY(a.joined_date)}</td>
                    <td><span className={`badge ${a.keys_issued ? 'badge-green' : 'badge-amber'}`}>{a.keys_issued ? 'Yes' : 'No'}</span></td>
                    <td>
                      {a.rules_form_submitted === 'yes'
                        ? <span className="badge badge-green">✓ Yes</span>
                        : a.rules_form_submitted === 'no'
                        ? <span className="badge badge-red">✗ No</span>
                        : <span className="badge badge-gray">—</span>
                      }
                    </td>
                    <td>
                      {canEditRow(a.students?.batch_name) && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => openEdit(a)}><Edit2 size={13} /></button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} title="Delete" onClick={async () => {
                            { const blk = await hostelBillsBlockDelete(a); if (blk) { toast.error(blk, { duration: 9000 }); return; } }
                            if (!window.confirm(`Delete hostel record for ${a.student_name || 'this student'}?`)) return;
                            const { error } = await supabase.from('hostel_allocations').delete().eq('id', a.id);
                            if (error) toast.error(error.message);
                            else { toast.success('Deleted ✅'); loadAll(); }
                          }}><Trash2 size={13} /></button>
                          <button className="btn btn-secondary btn-sm" onClick={() => openBills(a)}>Utility Bills</button>
                          <button className="btn btn-danger btn-sm" onClick={() => setVacateConfirm(a)}>Vacate</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Vacated Tab */}
      {activeTab === 'vacated' && (
        loading ? <div className="loading"><div className="spinner" /></div> : (
        <div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:10}}>
            <input placeholder="🔍 Search name / reg no..." value={search} onChange={e=>setSearch(e.target.value)}
              style={{fontSize:12,padding:'5px 10px',border:'1px solid var(--border)',borderRadius:6,minWidth:220}}/>
            <select value={filterBatch} onChange={e=>setFilterBatch(e.target.value)} style={{fontSize:12,padding:'4px 8px'}}>
              <option value="">All Batches</option>
              {orderBatchNames([...new Set(vacatedAllocations.map(a=>a.students?.batch_name).filter(Boolean))]).map(b=>
                <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={vacatedHostelFilter} onChange={e=>setVacatedHostelFilter(e.target.value)} style={{fontSize:12,padding:'4px 8px'}}>
              <option value="">All Hostels</option>
              {hostels.map(h=><option key={h.id} value={h.hostel_name}>{h.hostel_name}</option>)}
            </select>
            <span style={{fontSize:11,color:'var(--text3)'}}>{vacatedAllocations.length} vacated</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Student</th><th>Hostel</th><th>Room</th><th>Joined Date</th><th>Vacate Date</th><th>Batch</th><th>Actions</th></tr></thead>
              <tbody>
                {vacatedAllocations.filter(a => {
                  const q = search.toLowerCase();
                  return (!q || a.students?.full_name?.toLowerCase().includes(q) || a.students?.register_number?.toLowerCase().includes(q))
                    && (!filterBatch || a.students?.batch_name === filterBatch)
                    && (!vacatedHostelFilter || a.hostels?.hostel_name === vacatedHostelFilter);
                }).length === 0 ? (
                  <tr><td colSpan={6}><div className="empty-state"><Building2 /><h3>No vacated students</h3></div></td></tr>
                ) : vacatedAllocations.filter(a => {
                  const q = search.toLowerCase();
                  return (!q || a.students?.full_name?.toLowerCase().includes(q) || a.students?.register_number?.toLowerCase().includes(q))
                    && (!filterBatch || a.students?.batch_name === filterBatch)
                    && (!vacatedHostelFilter || a.hostels?.hostel_name === vacatedHostelFilter);
                }).map(a => (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{a.students?.full_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>{a.students?.register_number}</div>
                    </td>
                    <td><span className="badge badge-gray">{a.hostels?.hostel_name}</span></td>
                    <td>{a.room_number || '—'}</td>
                    <td style={{ fontSize: 12 }}>{fmtDMY(a.joined_date) || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--red)', fontWeight: 600 }}>{fmtDMY(a.vacate_date) || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>{a.students?.batch_name || '—'}</td>
                    <td>
                      {canEditRow(a.students?.batch_name) && (
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                          onClick={async () => {
                            { const blk = await hostelBillsBlockDelete(a); if (blk) { toast.error(blk, { duration: 9000 }); return; } }
                            if (!window.confirm(`Delete hostel record for ${a.students?.full_name}? This cannot be undone.`)) return;
                            const { error } = await supabase.from('hostel_allocations').delete().eq('id', a.id);
                            if (error) toast.error(error.message);
                            else { toast.success('Record deleted'); loadAll(); }
                          }}
                          title="Delete">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )
      )}

      {/* ALLOCATE MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">{editId ? 'Edit Hostel Allocation' : 'Allocate Hostel'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowModal(false); setEditId(null); setForm(emptyForm); }}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-section-heading">Allocation Details</div>
              <div className="form-grid">
                <div className="form-group"><label>Filter by Batch</label>
                  <SearchableSelect
                    options={batches.filter(b=>ownBatch(b.batch_name)).map(b=>({value:b.batch_name,label:b.batch_name}))}
                    value={form._modalBatch || ''}
                    onChange={v => setForm(f => ({ ...f, _modalBatch: v, student_id: '' }))}
                    placeholder="All Batches"
                    searchPlaceholder="Search batch..."
                  />
                </div>
                <div className="form-group"><label>Student *</label>
                  <SearchableSelect
                    options={(form._modalBatch ? students.filter(s => (s.batches?.batch_name||s.batch_name)===form._modalBatch||s.batch_id===form._modalBatch) : students).filter(s => ownBatch(s.batch_name || s.batches?.batch_name)).slice().sort(byRegNo).map(s => ({ value: s.id, label: `${s.full_name} (${s.register_number})` }))}
                    value={form.student_id}
                    onChange={v => setForm(f => ({ ...f, student_id: v }))}
                    placeholder="Select Student"
                    searchPlaceholder="Search student..."
                  />
                </div>

                {/* Auto-filled student details */}
                {form.student_id && (() => {
                  const s = students.find(st => st.id === form.student_id);
                  if (!s) return null;
                  return (
                    <div className="form-group full">
                      <div style={{ background: 'var(--accent-light)', border: '1px solid var(--accent)', borderRadius: 8, padding: '10px 14px', fontSize: 12, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                        {s.contact_number && <span>📞 <strong><PhoneField phone={s.contact_number}/></strong></span>}
                        {s.whatsapp_number && <span>💬 WhatsApp: <strong>{s.whatsapp_number}</strong></span>}
                        {s.guardian_name && <span>👤 Guardian: <strong>{s.guardian_name}</strong></span>}
                        {s.course_packages?.course_name && <span>📦 Package: <strong>{s.course_packages.course_name}</strong></span>}
                      </div>
                    </div>
                  );
                })()}
                <div className="form-group"><label>Hostel *</label>
                  <select value={form.hostel_id} onChange={e => setForm(f => ({ ...f, hostel_id: e.target.value }))}>
                    <option value="">Select Hostel</option>
                    {hostels.map(h => <option key={h.id} value={h.id}>{h.hostel_name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Hostel Request & Rules Form</label>
                  <select value={form.rules_form_submitted} onChange={e => setForm(f => ({ ...f, rules_form_submitted: e.target.value }))}>
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div className="form-group"><label>Room Number</label><input value={form.room_number} onChange={e => setForm(f => ({ ...f, room_number: e.target.value }))} /></div>
                <div className="form-group"><label>Joining Date</label><DateInput value={form.joined_date} onChange={e => setForm(f => ({ ...f, joined_date: e.target.value }))} /></div>
                <div className="form-group"><label>Keys Collected (Y/N)</label>
                  <select value={form.keys_issued ? 'yes' : 'no'} onChange={e => setForm(f => ({ ...f, keys_issued: e.target.value === 'yes' }))}>
                    <option value="no">No</option><option value="yes">Yes</option>
                  </select>
                </div>
                <div className="form-group"><label>Hostel Status</label>
                  <select value={form.hostel_status || 'Active'} onChange={e => setForm(f => ({ ...f, hostel_status: e.target.value }))}>
                    <option value="Active">Active</option>
                    <option value="Vacated">Vacated</option>
                  </select>
                </div>
                <div className="form-group"><label>Keys Returned (Y/N)</label>
                  <select value={form.keys_returned ? 'yes' : 'no'} onChange={e => setForm(f => ({ ...f, keys_returned: e.target.value === 'yes' }))}>
                    <option value="no">No</option><option value="yes">Yes</option>
                  </select>
                </div>
                <div className="form-group"><label>Hostel Vacate Date</label>
                  <DateInput value={form.hostel_vacate_date || ''} onChange={e => setForm(f => ({ ...f, hostel_vacate_date: e.target.value }))} />
                </div>
                <div className="form-group full"><label>Remarks</label><input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} /></div>
              </div>

              {/* Monthly Rent Amount Section */}
              <div className="form-section-heading" style={{ marginTop: 16 }}>Monthly Rent Amount</div>
              <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text3)' }}>Record the rent amount for each month of stay</div>
              {(form.rent_payments || []).map((p, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, marginBottom: 8, background: 'var(--bg3)', padding: 10, borderRadius: 8, alignItems: 'end' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: 11 }}>Month</label>
                    <select value={p.month} onChange={e => { const r=[...form.rent_payments]; r[i].month=e.target.value; setForm(f=>({...f,rent_payments:r})); }}>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m,mi)=><option key={m} value={mi+1}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: 11 }}>Rent Amount (₹)</label>
                    <input type="number" value={p.amount} onChange={e => { const r=[...form.rent_payments]; r[i].amount=e.target.value; setForm(f=>({...f,rent_payments:r})); }} placeholder="0" />
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ color:'var(--red)', marginBottom: 2 }} onClick={() => setForm(f=>({...f,rent_payments:f.rent_payments.filter((_,j)=>j!==i)}))}>
                    <X size={13}/>
                  </button>
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={() => setForm(f=>({...f,rent_payments:[...(f.rent_payments||[]),{month:new Date().getMonth()+1,amount:''}]}))}>
                <Plus size={12}/> Add Month
              </button>

              {/* Total Amount — always show when editing */}
              {(() => {
                const totalRent = (form.rent_payments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
                return (
                  <div style={{ marginTop: 14, padding: '14px 18px', background: totalRent > 0 ? 'var(--accent-light)' : 'var(--bg3)', border: `1.5px solid ${totalRent > 0 ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>
                        Total Hostel Rent ({(form.rent_payments||[]).length} month{(form.rent_payments||[]).length !== 1 ? 's' : ''})
                      </div>
                      <div style={{ fontWeight: 800, fontSize: 20, color: totalRent > 0 ? 'var(--accent)' : 'var(--text3)' }}>
                        ₹{totalRent.toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'right' }}>
                      ↳ This total will be saved<br/>to student's Hostel Fees field
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowModal(false); setEditId(null); setForm(emptyForm); }}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Allocate'}</button>
            </div>
          </div>
        </div>
      )}

      {/* BULK UTILITY BILL MODAL */}
      {showBulkBillModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowBulkBillModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">⚡ Bulk Add Utility Bills</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowBulkBillModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gap:12}}>
                <div>
                  <label className="form-label">Hostel *</label>
                  <select className="form-control" value={bulkBillForm.hostel_id} onChange={e=>setBulkBillForm(f=>({...f,hostel_id:e.target.value}))}>
                    <option value="">Select Hostel</option>
                    {hostels.map(h=><option key={h.id} value={h.id}>{h.hostel_name}</option>)}
                  </select>
                  {bulkBillForm.hostel_id && <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
                    {allocations.filter(a=>a.hostel_id===bulkBillForm.hostel_id).length} students will be billed
                  </div>}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  <div>
                    <label className="form-label">Month *</label>
                    <select className="form-control" value={bulkBillForm.billing_month} onChange={e=>setBulkBillForm(f=>({...f,billing_month:e.target.value}))}>
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Year *</label>
                    <input className="form-control" type="number" value={bulkBillForm.billing_year} onChange={e=>setBulkBillForm(f=>({...f,billing_year:e.target.value}))}/>
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                  <div>
                    <label className="form-label">⚡ Electricity (₹)</label>
                    <input className="form-control" type="number" placeholder="0" value={bulkBillForm.electricity_amount} onChange={e=>setBulkBillForm(f=>({...f,electricity_amount:e.target.value}))}/>
                  </div>
                  <div>
                    <label className="form-label">💧 Water (₹)</label>
                    <input className="form-control" type="number" placeholder="0" value={bulkBillForm.water_amount} onChange={e=>setBulkBillForm(f=>({...f,water_amount:e.target.value}))}/>
                  </div>
                  <div>
                    <label className="form-label">Other (₹)</label>
                    <input className="form-control" type="number" placeholder="0" value={bulkBillForm.other_amount} onChange={e=>setBulkBillForm(f=>({...f,other_amount:e.target.value}))}/>
                  </div>
                </div>
                <div style={{background:'var(--surface2)',borderRadius:6,padding:'8px 12px',fontSize:12}}>
                  Total per student: <strong>₹{(Number(bulkBillForm.electricity_amount||0)+Number(bulkBillForm.water_amount||0)+Number(bulkBillForm.other_amount||0)).toLocaleString('en-IN')}</strong>
                  {bulkBillForm.hostel_id && <span style={{marginLeft:12}}>
                    Total: <strong>₹{((Number(bulkBillForm.electricity_amount||0)+Number(bulkBillForm.water_amount||0)+Number(bulkBillForm.other_amount||0))*allocations.filter(a=>a.hostel_id===bulkBillForm.hostel_id).length).toLocaleString('en-IN')}</strong>
                  </span>}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowBulkBillModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveBulkBills} disabled={bulkBillSaving}>
                {bulkBillSaving?'Saving...':'⚡ Add Bills to All Students'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* UTILITY BILL MODAL */}
      {showBillModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowBillModal(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">Utility Bills — {showBillModal.students?.full_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowBillModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <div style={{ fontWeight: 600, marginBottom: 12 }}>Add / Update Bill</div>
                <div className="form-grid">
                  <div className="form-group"><label>Month</label>
                    <select value={billForm.billing_month} onChange={e => setBillForm(f => ({ ...f, billing_month: e.target.value }))}>
                      {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Year</label><input type="number" value={billForm.billing_year} onChange={e => setBillForm(f => ({ ...f, billing_year: e.target.value }))} /></div>
                  <div className="form-group"><label>Electricity (₹)</label><input type="number" value={billForm.electricity_amount} onChange={e => setBillForm(f => ({ ...f, electricity_amount: e.target.value }))} /></div>
                  <div className="form-group"><label>Water (₹)</label><input type="number" value={billForm.water_amount} onChange={e => setBillForm(f => ({ ...f, water_amount: e.target.value }))} /></div>
                  <div className="form-group"><label>Other (₹)</label><input type="number" value={billForm.other_amount} onChange={e => setBillForm(f => ({ ...f, other_amount: e.target.value }))} /></div>
                  <div className="form-group"><label>Payment Status</label>
                    <select value={billForm.payment_status} onChange={e => setBillForm(f => ({ ...f, payment_status: e.target.value }))}>
                      <option value="unpaid">Unpaid</option><option value="paid">Paid</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-primary btn-sm" onClick={saveBill}>Save Bill</button>
                </div>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Month</th><th>Electricity</th><th>Water</th><th>Other</th><th>Total</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {bills.length === 0 ? <tr><td colSpan={6}><div className="empty-state" style={{ padding: 20 }}><p>No bills recorded</p></div></td></tr>
                      : bills.map(b => (
                        <tr key={b.id}>
                          <td>{MONTHS[b.billing_month - 1]} {b.billing_year}</td>
                          <td>₹{b.electricity_amount}</td><td>₹{b.water_amount}</td><td>₹{b.other_amount}</td>
                          <td style={{ fontWeight: 700 }}>₹{b.total_amount}</td>
                          <td><span className={`badge ${b.payment_status === 'paid' ? 'badge-green' : 'badge-red'}`}>{b.payment_status}</span></td>
                          <td style={{display:'flex',gap:4}}>
                            {canEditRow(showBillModal?.students?.batch_name)&&<button className="btn btn-ghost btn-sm" style={{color:'var(--accent)'}} onClick={()=>setBillForm({billing_month:b.billing_month,billing_year:b.billing_year,electricity_amount:b.electricity_amount||'',water_amount:b.water_amount||'',other_amount:b.other_amount||'',payment_status:b.payment_status||'unpaid'})} title="Edit bill"><Edit2 size={13}/></button>}
                            {canEditRow(showBillModal?.students?.batch_name)&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>deleteBill(b.id)} title="Delete bill"><Trash2 size={13}/></button>}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {bills.length > 0 && (
                <div style={{ marginTop: 12, textAlign: 'right', fontSize: 13, color: 'var(--text2)' }}>
                  Total Bills: <strong style={{ color: 'var(--text)' }}>₹{bills.reduce((a, b) => a + Number(b.total_amount), 0).toLocaleString('en-IN')}</strong>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowBillModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* VACATE CONFIRMATION POPUP */}
      {vacateConfirm && (
        <div className="modal-overlay" onClick={() => setVacateConfirm(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Confirm Vacate</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setVacateConfirm(null)}><X size={16} /></button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '28px 24px' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🏠</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
                {vacateConfirm.students?.full_name}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>
                {vacateConfirm.hostels?.hostel_name} · Room {vacateConfirm.room_number || '—'}
              </div>
              <div style={{ fontSize: 14, color: 'var(--text2)', padding: '12px 16px', background: 'var(--bg3)', borderRadius: 8, marginTop: 12 }}>
                Please confirm that this student has vacated the hostel.
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center', gap: 12 }}>
              <button className="btn btn-secondary" onClick={() => setVacateConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmVacate}>✓ Yes, Vacate</button>
            </div>
          </div>
        </div>
      )}
      {activeTab === 'bills' && <HostelBills />}
      {activeTab === 'monthly' && (
        <div>
          <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end',marginBottom:12}}>
            <div className="form-group" style={{marginBottom:0}}>
              <label style={{fontSize:11}}>Hostel</label>
              <select value={mFilter.hostel} onChange={e=>{setMFilter(f=>({...f,hostel:e.target.value}));loadMonthlyCharges(e.target.value,mFilter.month,mFilter.year);}} style={{fontSize:12}}>
                <option value="">All Hostels</option>
                {hostels.map(h=><option key={h.id} value={h.id}>{h.hostel_name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label style={{fontSize:11}}>Month</label>
              <select value={mFilter.month} onChange={e=>{setMFilter(f=>({...f,month:e.target.value?Number(e.target.value):''}));loadMonthlyCharges(mFilter.hostel,e.target.value?Number(e.target.value):'',mFilter.year);}} style={{fontSize:12}}>
                <option value="">All Months</option>
                {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label style={{fontSize:11}}>Year</label>
              <select value={mFilter.year} onChange={e=>{setMFilter(f=>({...f,year:Number(e.target.value)}));loadMonthlyCharges(mFilter.hostel,mFilter.month,Number(e.target.value));}} style={{fontSize:12}}>
                {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={()=>loadMonthlyCharges(mFilter.hostel,mFilter.month,mFilter.year)}>🔍 Load</button>
            {!batchRestricted && <button className="btn btn-primary btn-sm" onClick={()=>setShowAddCharge(true)}>+ Add Monthly Charges</button>}
          </div>
          {monthlyLoading ? <div className="loading"><div className="spinner"/></div> : (
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr style={{background:'var(--accent-light)'}}>
                {['Student','Reg No','Batch','Hostel','Month/Year','Amount','Paid','Status','Remarks','Action'].map(h=>
                  <th key={h} style={{padding:'8px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {monthlyCharges.length===0 ? (
                  <tr><td colSpan={10} style={{padding:20,textAlign:'center',color:'var(--text3)'}}>No charges found. Select filters and click Load, or Add Monthly Charges.</td></tr>
                ) : monthlyCharges.map((c,i)=>(
                  <tr key={c.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                    <td style={{padding:'6px 10px',fontWeight:500}}>{c.students?.full_name||'—'}</td>
                    <td style={{padding:'6px 10px'}}>{c.students?.register_number||'—'}</td>
                    <td style={{padding:'6px 10px'}}>{c.students?.batch_name||'—'}</td>
                    <td style={{padding:'6px 10px'}}>{c.hostel_name||'—'}</td>
                    <td style={{padding:'6px 10px'}}>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][c.month-1]} {c.year}</td>
                    <td style={{padding:'6px 10px',fontWeight:600}}>₹{c.amount}</td>
                    <td style={{padding:'6px 10px',color:'var(--green)',fontWeight:600}}>{c.paid_amount>0?`₹${c.paid_amount}`:'-'}</td>
                    <td style={{padding:'6px 10px'}}>
                      <span style={{padding:'2px 8px',borderRadius:8,fontSize:10,fontWeight:600,
                        background:c.status==='Paid'?'var(--green-light)':c.status==='Partial'?'var(--amber-light)':'var(--red-light)',
                        color:c.status==='Paid'?'var(--green)':c.status==='Partial'?'var(--amber)':'var(--red)'}}>{c.status||'Unpaid'}</span>
                    </td>
                    <td style={{padding:'6px 10px',color:'var(--text3)'}}>{c.remarks||'—'}</td>
                    <td style={{padding:'6px 10px'}}>
                      {ownBatch(c.students?.batch_name) && <><button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--green)'}} onClick={async()=>{
                        const amt = prompt('Enter paid amount:');
                        if(!amt) return;
                        const paid = Number(amt);
                        const status = paid>=c.amount?'Paid':paid>0?'Partial':'Unpaid';
                        await supabase.from('hostel_monthly_charges').update({paid_amount:paid,status,paid_date:new Date().toISOString().split('T')[0]}).eq('id',c.id);
                        loadMonthlyCharges(mFilter.hostel,mFilter.month,mFilter.year);
                      }}>💰 Pay</button>
                      <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--red)',marginLeft:4}} onClick={async()=>{
                        if(!window.confirm('Delete this charge?')) return;
                        await supabase.from('hostel_monthly_charges').delete().eq('id',c.id);
                        loadMonthlyCharges(mFilter.hostel,mFilter.month,mFilter.year);
                      }}>✕</button></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>)}
          {monthlyCharges.length>0&&<div style={{marginTop:8,fontSize:12,color:'var(--text3)',fontWeight:600}}>
            Total: ₹{monthlyCharges.reduce((s,c)=>s+Number(c.amount||0),0).toLocaleString()} | 
            Collected: ₹{monthlyCharges.reduce((s,c)=>s+Number(c.paid_amount||0),0).toLocaleString()} | 
            Balance: ₹{monthlyCharges.reduce((s,c)=>s+(Number(c.amount||0)-Number(c.paid_amount||0)),0).toLocaleString()}
          </div>}
        </div>
      )}
      {showAddCharge&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowAddCharge(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">🏠 Add Monthly Hostel Charges</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowAddCharge(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10,marginBottom:12}}>
                <div className="form-group" style={{marginBottom:0}}>
                  <label>Hostel *</label>
                  <select value={chargeForm.hostel_id} onChange={e=>setChargeForm(f=>({...f,hostel_id:e.target.value}))} style={{fontSize:12}}>
                    <option value="">Select Hostel</option>
                    {hostels.map(h=><option key={h.id} value={h.id}>{h.hostel_name}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{marginBottom:0}}>
                  <label>Month *</label>
                  <select value={chargeForm.month} onChange={e=>setChargeForm(f=>({...f,month:Number(e.target.value)}))} style={{fontSize:12}}>
                    {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{marginBottom:0}}>
                  <label>Year *</label>
                  <select value={chargeForm.year} onChange={e=>setChargeForm(f=>({...f,year:Number(e.target.value)}))} style={{fontSize:12}}>
                    {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{marginBottom:0}}>
                  <label>Monthly Rate (₹) *</label>
                  <input type="number" value={chargeForm.amount} onChange={e=>setChargeForm(f=>({...f,amount:e.target.value}))} placeholder="e.g. 3000" style={{fontSize:12}}/>
                </div>
              </div>
              <div style={{display:'flex',gap:8,marginBottom:12}}>
                <button className="btn btn-secondary btn-sm" onClick={async()=>{
                  if(!chargeForm.hostel_id||!chargeForm.amount){toast.error('Select hostel and enter monthly rate');return;}
                  setChargeStudentsLoading(true);
                  const daysInMonth = new Date(chargeForm.year, chargeForm.month, 0).getDate();
                  const monthStart = new Date(chargeForm.year, chargeForm.month-1, 1);
                  const monthEnd = new Date(chargeForm.year, chargeForm.month-1, daysInMonth);
                  const {data:allocs} = await supabase.from('hostel_allocations')
                    .select('id,student_id,joined_date,vacate_date,hostel_status,students(full_name,register_number,batch_name)')
                    .eq('hostel_id',chargeForm.hostel_id);
                  const rate = Number(chargeForm.amount);
                  const students = (allocs||[]).filter(a=>{
                    const joined = new Date(a.joined_date);
                    const vacated = a.vacate_date ? new Date(a.vacate_date) : null;
                    // Include if joined before month end and (active OR vacated after month start)
                    return joined <= monthEnd && (!vacated || vacated >= monthStart);
                  }).map(a=>{
                    const joined = new Date(a.joined_date);
                    const vacated = a.vacate_date ? new Date(a.vacate_date) : null;
                    const effectiveStart = joined < monthStart ? monthStart : joined;
                    const effectiveEnd = vacated && vacated < monthEnd ? vacated : monthEnd;
                    const days = Math.max(0, Math.round((effectiveEnd - effectiveStart)/(1000*60*60*24)) + 1);
                    const amount = days >= daysInMonth ? rate : Math.round((days/daysInMonth)*rate);
                    return {
                      student_id: a.student_id,
                      full_name: a.students?.full_name||'—',
                      register_number: a.students?.register_number||'—',
                      batch_name: a.students?.batch_name||'—',
                      joined_date: a.joined_date,
                      vacate_date: a.vacate_date,
                      hostel_status: a.hostel_status,
                      days, daysInMonth,
                      amount,
                      include: true,
                    };
                  });
                  setChargeStudents(students);
                  setChargeStudentsLoading(false);
                }}>📋 Load Students</button>
                <span style={{fontSize:11,color:'var(--text3)',alignSelf:'center'}}>
                  {chargeStudents.length>0 && `${chargeStudents.filter(s=>s.include).length} students | Total: ₹${chargeStudents.filter(s=>s.include).reduce((s,c)=>s+Number(c.amount||0),0).toLocaleString()}`}
                </span>
              </div>
              {chargeStudentsLoading && <div style={{textAlign:'center',padding:12}}>Loading...</div>}
              {chargeStudents.length>0 && (
                <div style={{overflowX:'auto',maxHeight:320,overflowY:'auto',marginBottom:12}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                    <thead style={{position:'sticky',top:0}}><tr style={{background:'var(--accent-light)'}}>
                      <th style={{padding:'4px 6px'}}><input type="checkbox" checked={chargeStudents.every(s=>s.include)} onChange={e=>setChargeStudents(cs=>cs.map(s=>({...s,include:e.target.checked})))}/></th>
                      <th style={{padding:'4px 6px',textAlign:'left'}}>Student</th>
                      <th style={{padding:'4px 6px',textAlign:'left'}}>Reg No</th>
                      <th style={{padding:'4px 6px'}}>Status</th>
                      <th style={{padding:'4px 6px'}}>Join Date</th>
                      <th style={{padding:'4px 6px'}}>Vacate Date</th>
                      <th style={{padding:'4px 6px'}}>Days</th>
                      <th style={{padding:'4px 6px'}}>Amount (₹)</th>
                    </tr></thead>
                    <tbody>
                      {chargeStudents.map((s,i)=>(
                        <tr key={s.student_id} style={{borderBottom:'1px solid var(--border)',background:s.include?'':'var(--surface2)',opacity:s.include?1:0.5}}>
                          <td style={{padding:'4px 6px',textAlign:'center'}}><input type="checkbox" checked={s.include} onChange={e=>setChargeStudents(cs=>cs.map((x,j)=>j===i?{...x,include:e.target.checked}:x))}/></td>
                          <td style={{padding:'4px 6px',fontWeight:500}}>{s.full_name}</td>
                          <td style={{padding:'4px 6px'}}>{s.register_number}</td>
                          <td style={{padding:'4px 6px',textAlign:'center'}}><span style={{fontSize:9,padding:'1px 5px',borderRadius:4,background:s.hostel_status==='Active'?'var(--green-light)':'var(--red-light)',color:s.hostel_status==='Active'?'var(--green)':'var(--red)'}}>{s.hostel_status}</span></td>
                          <td style={{padding:'4px 6px',textAlign:'center'}}>{fmtDMY(s.joined_date)||'—'}</td>
                          <td style={{padding:'4px 6px',textAlign:'center'}}>{fmtDMY(s.vacate_date)||'—'}</td>
                          <td style={{padding:'4px 6px',textAlign:'center'}}>{s.days}/{s.daysInMonth}</td>
                          <td style={{padding:'4px 6px',textAlign:'right'}}>
                            <input type="number" value={s.amount} onChange={e=>setChargeStudents(cs=>cs.map((x,j)=>j===i?{...x,amount:Number(e.target.value)}:x))} style={{width:70,fontSize:11,padding:'2px 4px',textAlign:'right'}}/>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="form-group" style={{marginBottom:0}}>
                <label>Remarks</label>
                <input value={chargeForm.remarks} onChange={e=>setChargeForm(f=>({...f,remarks:e.target.value}))} placeholder="Optional..." style={{fontSize:12}}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>{setShowAddCharge(false);setChargeStudents([]); }}>Cancel</button>
              <button className="btn btn-primary" disabled={chargeSaving||chargeStudents.filter(s=>s.include).length===0} onClick={async()=>{
                const toSave = chargeStudents.filter(s=>s.include&&s.amount>0);
                if(!toSave.length){toast.error('No students selected');return;}
                setChargeSaving(true);
                const hostel = hostels.find(h=>h.id===chargeForm.hostel_id);
                const rows = toSave.map(s=>({
                  student_id: s.student_id,
                  hostel_id: chargeForm.hostel_id,
                  hostel_name: hostel?.hostel_name||'',
                  month: chargeForm.month,
                  year: chargeForm.year,
                  amount: s.amount,
                  status: 'Unpaid',
                  remarks: chargeForm.remarks||'',
                }));
                const {error} = await supabase.from('hostel_monthly_charges').upsert(rows,{onConflict:'student_id,month,year,hostel_id'});
                if(error){toast.error(error.message);}
                else{
                  // Also update hostel_utility_bills so fees page shows the amount
                  const utilRows = rows.map(r=>({
                    student_id: r.student_id,
                    billing_month: r.month,
                    billing_year: r.year,
                    other_amount: r.amount,
                  }));
                  for (const ur of utilRows) {
                    const {data:ex} = await supabase.from('hostel_utility_bills')
                      .select('id,electricity_amount,water_amount')
                      .eq('student_id',ur.student_id)
                      .eq('billing_month',ur.billing_month)
                      .eq('billing_year',ur.billing_year)
                      .single();
                    await supabase.from('hostel_utility_bills').upsert({
                      ...ur,
                      electricity_amount: ex?.electricity_amount||0,
                      water_amount: ex?.water_amount||0,
                    },{onConflict:'student_id,billing_month,billing_year'});
                  }
                  toast.success(`Charges saved for ${rows.length} students`);
                  setShowAddCharge(false);setChargeStudents([]);loadMonthlyCharges(mFilter.hostel,mFilter.month,mFilter.year);
                }
                setChargeSaving(false);
              }}>{chargeSaving?'Saving...':`Save Charges (${chargeStudents.filter(s=>s.include).length} students)`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// HOSTEL KSEB & KWA BILLS
// ─────────────────────────────────────────────────────────────────

// Fixed hostel connections from Excel
const HOSTEL_CONNECTIONS = [
    { hostel:'Mamangalam',  type:'KSEB', name:'Mamangalam KSEB',              cons_no:'1155 4360 10140', floor:'',             owner:'Surendran' },
  { hostel:'Mamangalam',  type:'KWA',  name:'Mamangalam KWA',               cons_no:'3113 11 6248 | E34/1270/N',    floor:'',             owner:'Surendran' },
  { hostel:'Vazhakkala',  type:'KSEB', name:'Vazhakkala First KSEB',        cons_no:'1157 3140 27896', floor:'First Floor',  owner:'Abdul Samad' },
  { hostel:'Vazhakkala',  type:'KSEB', name:'Vazhakkala Second KSEB',       cons_no:'1157 3160 27898', floor:'Second Floor', owner:'Abdul Samad' },
  { hostel:'Vazhakkala',  type:'KWA',  name:'Vazhakkala KWA',               cons_no:'3224 21 9796 | TKK/19992/N',    floor:'',             owner:'Abdul Samad' },
  { hostel:'Padamugal',   type:'KSEB', name:'Padamugal KSEB 1',             cons_no:'1157 3120 17330', floor:'1',            owner:'Shameer K A' },
  { hostel:'Padamugal',   type:'KSEB', name:'Padamugal KSEB 2',             cons_no:'1157 3120 17329', floor:'2',            owner:'Shameer K A' },
  { hostel:'Padamugal',   type:'KSEB', name:'Padamugal KSEB 3',             cons_no:'1157 3180 17331', floor:'3',            owner:'Shameer K A' },
  { hostel:'Padamugal',   type:'KWA',  name:'Padamugal KWA',                cons_no:'3224211133 | TKK/10787/N',                floor:'',             owner:'Shameer K A' },
];

const PAID_STATUS    = ['Yes','No','Hold'];
const CONTRIBUTION   = ['Students','Armstech','Half'];
const BILL_MONTHS    = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const HOSTELS        = ['Mamangalam','Vazhakkala','Padamugal'];

function HostelBills() {
  const { user: authUser, assignedBatches, isCoordinator, isAdmin } = useAuth();
  // KSEB / KWA bills belong to a whole hostel, not a batch — view-only for batch coordinators
  const billMail = String(authUser?.email || '').trim().toLowerCase();
  const batchRestricted = billMail !== OWNER_MAIL && !OFFICE_MAILS.includes(billMail) && !isAdmin && ((assignedBatches || []).length > 0 || !!isCoordinator);
  const [bills,      setBills]      = useState([]);
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [filterHostel, setFilterHostel] = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterMonth,  setFilterMonth]  = useState('');
  const [filterYear,   setFilterYear]   = useState(new Date().getFullYear());
  const [filterPaid,   setFilterPaid]   = useState('');
  const [splitBill, setSplitBill] = useState(null);
  const [splitStudents, setSplitStudents] = useState([]);
  const [companyContrib, setCompanyContrib] = useState(0);
  const [splitSaving, setSplitSaving] = useState(false);
  const [splitInfo, setSplitInfo] = useState({});      // bill id -> { students, total, billAmt } for bills already split
  const [filterSplit, setFilterSplit] = useState('');
  const [splitSavedRows, setSplitSavedRows] = useState(null);   // saved (locked) shares of a bill that is already split
  const HOSTEL_ID_MAP = {
    'Padamugal': '85bf87b1-0071-4642-9a26-d9bde67ca6e5',
    'Vazhakkala': '3ffcb72d-17b8-4104-bffa-96af9ee0aad8',
    'Mamangalam': 'f2e78348-2d61-4a98-8908-be248fbf2a8d',
  };

  const now = new Date();
  const emptyForm = {
    connection_name: '', hostel: '', bill_type: 'KSEB', cons_no: '',
    month_year: `${BILL_MONTHS[now.getMonth()]} ${now.getFullYear()}`,
    prev_meter_reading: '', meter_reading: '', prev_reading_date: '', consumption: '', bill_date: '', bill_due_date: '',
    bill_disconnection_date: '', bill_amount: '', paid_status: 'No',
    paid_amount: '', paid_date: '', contribution: 'Students',
    payment_type: 'Cash', payment_mode: '', category: 'Utilities', receipt_no: '',
    remarks: '',
  };
  const [form, setForm] = useState(emptyForm);
  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { loadBills(); loadSplits(); }, []);

  // Real-time updates (debounced)
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { try { loadBills(); loadSplits(); } catch(e){} }, 800); };
    const ch = supabase.channel('hostel-realtime')
      .on('postgres_changes', {event:'*', schema:'public', table:'hostel_allocations'}, () => _reload())
      .on('postgres_changes', {event:'*', schema:'public', table:'hostel_bills'}, () => _reload())
      .on('postgres_changes', {event:'*', schema:'public', table:'hostel_bill_splits'}, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);

  async function loadBills() {
    const { data } = await supabase
      .from('hostel_bills')
      .select('*')
      .order('bill_date', { ascending: false });
    setBills(data || []);
  }

  // Which bills have already been split among students (a split of combined bills marks every bill in the group)
  async function loadSplits() {
    try {
      let all = [], from = 0;
      for (;;) {
        const { data, error } = await supabase.from('hostel_bill_splits')
          .select('bill_id,combined_bill_ids,student_id,share_amount,bill_amount')
          .order('bill_id').order('student_id').range(from, from + 999);
        if (error || !data) break;
        all = all.concat(data);
        if (data.length < 1000) break;
        from += 1000;
      }
      const per = {};
      all.forEach(r => {
        let ids = r.combined_bill_ids;
        if (typeof ids === 'string') { try { ids = JSON.parse(ids); } catch (e) { ids = []; } }
        if (!Array.isArray(ids)) ids = [];
        const p = per[r.bill_id] = per[r.bill_id] || { ids: ids.length ? ids : [r.bill_id], src: r.bill_id, students: new Set(), total: 0, billAmt: Number(r.bill_amount) || 0 };
        p.students.add(r.student_id); p.total += Number(r.share_amount) || 0;
      });
      const info = {};
      Object.values(per).forEach(p => p.ids.forEach(id => { info[id] = { students: p.students.size, total: p.total, billAmt: p.billAmt, src: p.src }; }));
      setSplitInfo(info);
    } catch (e) { /* the Split column just stays empty */ }
  }

  async function save() {
    if (!form.connection_name || !form.month_year) {
      toast.error('Connection name and month/year are required');
      return;
    }
    setSaving(true);
    // blank text boxes must go to the database as NULL — '' is not valid for numeric / date columns
    const d = v => v && String(v).trim() ? v : null;
    const n = v => (v === '' || v == null || String(v).trim() === '' || isNaN(Number(v))) ? null : Number(v);
    const payload = {
      updated_by: authUser?.email, updated_at: new Date().toISOString(),
      ...form,
      bill_amount:        n(form.bill_amount),
      paid_amount:        n(form.paid_amount),
      meter_reading:      n(form.meter_reading),
      prev_meter_reading: n(form.prev_meter_reading),
      consumption:        n(form.consumption),
      bill_date:               d(form.bill_date),
      prev_reading_date:       d(form.prev_reading_date),
      bill_due_date:           d(form.bill_due_date),
      bill_disconnection_date: d(form.bill_disconnection_date),
      paid_date:               d(form.paid_date),
    };
    let savedId = editing;
    let error;
    if (editing) {
      ({ error } = await supabase.from('hostel_bills').update(payload).eq('id', editing));
    } else {
      const ins = await supabase.from('hostel_bills').insert(payload).select('id').single();
      error = ins.error; savedId = ins.data?.id;
    }
    if (error) { toast.error(error.message); setSaving(false); return; }

    // ── Auto-sync to Accounts as Cash Out ─────────────────────────
    if (form.paid_date && form.paid_status === 'Yes' && (form.paid_amount || form.bill_amount)) {
      try {
        // On edit, remove previous auto-synced entry for this bill
        if (editing) {
          await supabase.from('accounts_transactions')
            .delete()
            .eq('source', 'hostel_bills')
            .eq('reference', `hbill_${editing}`);
        }
        const syncAmt = Number(form.paid_amount || form.bill_amount || 0);
        const { error: accErr } = await supabase.from('accounts_transactions').insert({
          company:      'ARMSTECH',
          date:         form.paid_date,
          type:         'Cash Out',
          category:     form.category || 'Utilities',
          payment_mode: form.payment_mode || 'Cash',
          amount:       syncAmt,
          reference:    savedId ? `hbill_${savedId}` : form.receipt_no || '',
          description:  `${form.bill_type} Bill — ${form.connection_name} (${form.month_year})`,
          source:       'hostel_bills',
        });
        if (accErr) toast.error('Bill saved but Accounts sync failed: ' + accErr.message);
        else toast.success('Bill saved ✅ — synced to Accounts');
      } catch (e) {
        toast.error('Bill saved but sync error: ' + e.message);
      }
    } else {
      // bill no longer paid (or no paid date) — make sure an earlier auto-synced Accounts entry does not stay behind
      if (editing) {
        await supabase.from('accounts_transactions').delete().eq('source', 'hostel_bills').eq('reference', `hbill_${editing}`);
      }
      toast.success(editing ? 'Updated ✅' : 'Bill saved ✅');
    }
    setShowModal(false);
    await loadBills();
    setSaving(false);
  }

  function openNew() { setEditing(null); setForm(emptyForm); setShowModal(true); }
  function openEdit(r) {
    setEditing(r.id);
    setForm({ ...emptyForm, ...r,
      bill_amount:   r.bill_amount   != null ? String(r.bill_amount)   : '',
      paid_amount:   r.paid_amount   != null ? String(r.paid_amount)   : '',
      meter_reading: r.meter_reading != null ? String(r.meter_reading) : '',
      prev_meter_reading: r.prev_meter_reading != null ? String(r.prev_meter_reading) : '',
      prev_reading_date:  r.prev_reading_date || '',
      consumption:   r.consumption   != null ? String(r.consumption)   : '',
      payment_type:  r.payment_type  || 'Cash',
      payment_mode:  r.payment_mode  || '',
      category:      r.category      || 'Utilities',
      receipt_no:    r.receipt_no    || '',
    });
    setShowModal(true);
  }

  // Pre-fill connection details when connection name is selected
  function onConnectionSelect(name) {
    const conn = HOSTEL_CONNECTIONS.find(c => c.name === name);
    if (conn) {
      setForm(f => ({ ...f, connection_name: name, hostel: conn.hostel,
        bill_type: conn.type, cons_no: conn.cons_no }));
    } else {
      sf('connection_name', name);
    }
  }

  // Filter bills
  const filtered = bills.filter(b => {
    return (!filterHostel || b.hostel === filterHostel)
      && (!filterType   || b.bill_type === filterType)
      && (!filterPaid   || b.paid_status === filterPaid)
      && (!filterMonth  || (b.month_year || '').includes(filterMonth))
      && (!filterYear   || (b.month_year || '').includes(String(filterYear)))
      && (!filterSplit  || (filterSplit === 'yes' ? !!splitInfo[b.id] : !splitInfo[b.id]));
  });

  // Summary stats
  const totalBillAmt = filtered.reduce((s, b) => s + Number(b.bill_amount || 0), 0);
  const totalPaidAmt = filtered.filter(b => b.paid_status === 'Yes').reduce((s, b) => s + Number(b.paid_amount || b.bill_amount || 0), 0);
  const pendingCount = filtered.filter(b => b.paid_status === 'No').length;
  const overdueCount = filtered.filter(b => b.paid_status === 'No' && b.bill_due_date && new Date(b.bill_due_date) < new Date()).length;

  const notSplitCount = filtered.filter(b => !splitInfo[b.id]).length;
  // one Split button per hostel + bill type + month: it sits on the first row of the group that is shown
  const splitGroupKey = x => x.hostel + '|' + x.bill_type + '|' + x.month_year;
  const splitLeadIds = {};
  filtered.forEach(x => { const k = splitGroupKey(x); if (!(k in splitLeadIds)) splitLeadIds[k] = x.id; });

  const INR = v => v ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';

  const paidBadge = s => {
    if (s === 'Yes')  return <span className="badge badge-green"  style={{ fontSize: 10 }}>✅ Paid</span>;
    if (s === 'No')   return <span className="badge badge-red"    style={{ fontSize: 10 }}>❌ Unpaid</span>;
    if (s === 'Hold') return <span className="badge badge-amber"  style={{ fontSize: 10 }}>⏸ Hold</span>;
    return '—';
  };

  const typeBadge = t => (
    <span className={`badge ${t === 'KSEB' ? 'badge-amber' : 'badge-blue'}`} style={{ fontSize: 10 }}>
      {t === 'KSEB' ? '⚡ KSEB' : '💧 KWA'}
    </span>
  );

  return (
    <div>
      {/* Summary Cards */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        {[
          { label: 'Total Bill Amount',  value: INR(totalBillAmt),  color: 'var(--accent)'  },
          { label: 'Total Paid',         value: INR(totalPaidAmt),  color: 'var(--green)'   },
          { label: 'Pending Bills',      value: pendingCount,       color: 'var(--amber)'   },
          { label: '⚠️ Overdue Bills',   value: overdueCount,       color: 'var(--red)'     },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color, fontSize: 20 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Hostel Info Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: (window.innerWidth<=768)?'1fr 1fr':'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
        {HOSTELS.map(h => {
          const conns = HOSTEL_CONNECTIONS.filter(c => c.hostel === h);
          const hBills = bills.filter(b => b.hostel === h);
          const unpaid = hBills.filter(b => b.paid_status === 'No').length;
          return (
            <div key={h} style={{ background: 'var(--surface)', border: `2px solid ${unpaid > 0 ? 'var(--amber)' : 'var(--border)'}`,
              borderRadius: 10, padding: '12px 14px', cursor: 'pointer' }}
              onClick={() => setFilterHostel(filterHostel === h ? '' : h)}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                {filterHostel === h ? '✓ ' : ''}{h} Hostel
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>
                Owner: {conns[0]?.owner || '—'}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {conns.map(c => (
                  <span key={c.name} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4,
                    background: c.type === 'KSEB' ? '#fef3c7' : '#dbeafe',
                    color: c.type === 'KSEB' ? '#92400e' : '#1d4ed8' }}>
                    {c.type === 'KSEB' ? '⚡' : '💧'} {c.name.replace(h + ' ', '')} {c.cons_no ? <span style={{opacity:0.7}}>({c.cons_no})</span> : ''}
                  </span>
                ))}
              </div>
              {unpaid > 0 && (
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>
                  ⚠️ {unpaid} unpaid
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={{ width: 120, fontSize: 12, padding: '4px 6px' }} value={filterHostel} onChange={e => setFilterHostel(e.target.value)}>
            <option value="">All Hostels</option>
            {HOSTELS.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
          <select style={{ width: 90, fontSize: 12, padding: '4px 6px' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            <option value="KSEB">⚡ KSEB</option>
            <option value="KWA">💧 KWA</option>
          </select>
          <select style={{ width: 90, fontSize: 12, padding: '4px 6px' }} value={String(filterYear)} onChange={e => setFilterYear(e.target.value)}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select style={{ width: 100, fontSize: 12, padding: '4px 6px' }} value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
            <option value="">All Months</option>
            {BILL_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select style={{ width: 100, fontSize: 12, padding: '4px 6px' }} value={filterPaid} onChange={e => setFilterPaid(e.target.value)}>
            <option value="">All Status</option>
            <option value="Yes">✅ Paid</option>
            <option value="No">❌ Unpaid</option>
            <option value="Hold">⏸ Hold</option>
          </select>
          <select style={{ width: 110, fontSize: 12, padding: '4px 6px' }} value={filterSplit} onChange={e => setFilterSplit(e.target.value)}>
            <option value="">All Splits</option>
            <option value="yes">✅ Split</option>
            <option value="no">Not split</option>
          </select>
          {(filterHostel || filterType || filterMonth || filterPaid || filterSplit) &&
            <button className="btn btn-ghost btn-sm" onClick={() => { setFilterHostel(''); setFilterType(''); setFilterMonth(''); setFilterPaid(''); setFilterSplit(''); }}>✕ Clear</button>}
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>{filtered.length} bills · {notSplitCount} not split</span>
        </div>
        {!batchRestricted && <button className="btn btn-primary" onClick={openNew}><Plus size={14} /> Add Bill</button>}
      </div>

      {/* Bills Table */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Connection</th>
              <th>Hostel</th>
              <th>Type</th>
              <th>Month / Year</th>
              <th style={{ textAlign: 'right' }}>Present Reading</th>
              <th style={{ textAlign: 'right' }}>Consumption</th>
              <th>Reading Date</th>
              <th>Due Date</th>
              <th>Disconnection Date</th>
              <th style={{ textAlign: 'right' }}>Bill Amount</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Paid Amount</th>
              <th>Paid Date</th>
              <th>Contribution</th>
              <th>Split</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={18}>
                <div className="empty-state">
                  <span style={{ fontSize: 32 }}>⚡💧</span>
                  <h3>No bills found</h3>
                  <p>Add KSEB or KWA bills for hostels</p>
                </div>
              </td></tr>
            ) : filtered.map((b, i) => {
              const isOverdue = b.paid_status === 'No' && b.bill_due_date && new Date(b.bill_due_date) < new Date();
              return (
                <tr key={b.id} style={{ background: isOverdue ? 'rgba(220,38,38,0.04)' : undefined }}>
                  <td style={{ fontSize: 11, color: 'var(--text3)' }}>{i + 1}</td>
                  <td style={{ fontWeight: 600, fontSize: 12, minWidth: 140 }}>{b.connection_name}</td>
                  <td><span className="badge badge-blue" style={{ fontSize: 9 }}>{b.hostel}</span></td>
                  <td>{typeBadge(b.bill_type)}</td>
                  <td style={{ fontSize: 12, fontWeight: 600 }}>{b.month_year}</td>
                  <td style={{ textAlign: 'right', fontSize: 12 }}>{b.meter_reading ?? '—'}</td>
                  <td style={{ textAlign: 'right', fontSize: 12 }}>{b.consumption != null ? `${b.consumption} units` : '—'}</td>
                  <td style={{ fontSize: 11 }}>{fmtDMY(b.bill_date) || '—'}</td>
                  <td style={{ fontSize: 11, color: isOverdue ? 'var(--red)' : undefined, fontWeight: isOverdue ? 700 : undefined }}>
                    {fmtDMY(b.bill_due_date) || '—'}
                    {isOverdue && <div style={{ fontSize: 9, color: 'var(--red)' }}>OVERDUE</div>}
                  </td>
                  <td style={{ fontSize: 11 }}>{fmtDMY(b.bill_disconnection_date) || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{INR(b.bill_amount)}</td>
                  <td>{paidBadge(b.paid_status)}</td>
                  <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--green)' }}>{INR(b.paid_amount)}</td>
                  <td style={{ fontSize: 11 }}>{fmtDMY(b.paid_date) || '—'}</td>
                  <td>
                    {b.contribution ? (
                      <span className={`badge ${b.contribution === 'Students' ? 'badge-cyan' : b.contribution === 'Armstech' ? 'badge-blue' : 'badge-amber'}`} style={{ fontSize: 9 }}>
                        {b.contribution}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{(() => {
                    const si = splitInfo[b.id];
                    if (!si) return <span className="badge badge-amber" style={{ fontSize: 9 }} title="Not yet split among students">Not split</span>;
                    const grp = bills.filter(x => x.hostel === b.hostel && x.bill_type === b.bill_type && x.month_year === b.month_year).reduce((t, x) => t + (Number(x.bill_amount) || 0), 0);
                    const changed = si.billAmt > 0 && Math.abs(grp - si.billAmt) > 0.5;
                    return (
                      <div>
                        <span className="badge badge-green" style={{ fontSize: 9 }} title="Split among students and added to their utility bills">✅ Split</span>
                        <div style={{ color: 'var(--text3)', marginTop: 2 }}>{si.students} students · ₹{Math.round(si.total).toLocaleString('en-IN')}</div>
                        {changed && <div style={{ color: 'var(--red)', fontWeight: 700 }} title={'Split was made on ₹' + si.billAmt + ', bill is now ₹' + grp}>⚠ amount changed — split again</div>}
                      </div>
                    );
                  })()}</td>
                  <td style={{ fontSize: 11, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.remarks}>{b.remarks || '—'}</td>
                  <td>
                    {!batchRestricted && <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(b)}><Edit2 size={13} /></button>
                      {splitLeadIds[splitGroupKey(b)] !== b.id
                        ? <span style={{ fontSize: 9, color: 'var(--text3)', alignSelf: 'center', whiteSpace: 'nowrap' }} title="All connections of this hostel, bill type and month are split together from the first row">↳ split with group</span>
                        : <button className="btn btn-ghost btn-sm" style={{color:'var(--cyan)',fontSize:10}} title="Split Bill (all connections of this hostel, type and month together)" onClick={async()=>{
                        const hostelId = HOSTEL_ID_MAP[b.hostel];
                        if (!hostelId) return toast.error('Hostel not found');
                        if (!b.prev_reading_date || !b.bill_date) return toast.error('Need previous & present reading dates for split');
                        // Combine all bills of same type+hostel+month_year
                        const samePeriodBills = bills.filter(x => x.hostel===b.hostel && x.bill_type===b.bill_type && x.month_year===b.month_year);
                        const combinedAmount = samePeriodBills.reduce((s,x)=>s+(Number(x.bill_amount)||0),0);
                        const combinedBillIds = samePeriodBills.map(x=>x.id);
                        { // a split made before all connections have a bill would use an incomplete total
                          const expectedConn = HOSTEL_CONNECTIONS.filter(c => c.hostel === b.hostel && c.type === b.bill_type).length;
                          const enteredConn = new Set(samePeriodBills.map(x => x.connection_name)).size;
                          if (!splitInfo[b.id] && expectedConn > 1 && enteredConn < expectedConn &&
                              !window.confirm('Only ' + enteredConn + ' of ' + expectedConn + ' ' + b.bill_type + ' connections of ' + b.hostel + ' have a bill entered for ' + b.month_year + '.\n\nSplitting now uses an incomplete total. Continue anyway?')) return;
                        }
                        const {data:allocs} = await supabase.from('hostel_allocations')
                          .select('id,student_id,joined_date,vacate_date,hostel_status,students(full_name,register_number)')
                          .eq('hostel_id', hostelId);
                        const si = splitInfo[b.id];
                        let savedRows = null;
                        if (si) {
                          const { data: sv } = await supabase.from('hostel_bill_splits').select('*').eq('bill_id', si.src || b.id).order('register_number');
                          if (sv && sv.length) savedRows = sv;
                        }
                        setSplitSavedRows(savedRows);
                        setSplitStudents(allocs||[]);
                        setCompanyContrib(0);
                        setSplitBill({...b, bill_amount:combinedAmount, combined_bill_ids:combinedBillIds, combined_count:samePeriodBills.length, saved_src: si ? si.src : null});
                      }}>{splitInfo[b.id] ? '🔒 Saved split' : '⚡ Split' + (bills.filter(x => splitGroupKey(x) === splitGroupKey(b)).length > 1 ? ' all (' + bills.filter(x => splitGroupKey(x) === splitGroupKey(b)).length + ')' : '')}</button>}
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={async () => {
                        if (!window.confirm('Delete this bill?')) return;
                        const { error: delErr } = await supabase.from('hostel_bills').delete().eq('id', b.id);
                        if (delErr) { toast.error(delErr.message); return; }
                        await supabase.from('accounts_transactions').delete().eq('source', 'hostel_bills').eq('reference', `hbill_${b.id}`);
                        loadBills();
                      }}><Trash2 size={13} /></button>
                    </div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr style={{ background: 'var(--bg3)', fontWeight: 700 }}>
                <td colSpan={10} style={{ textAlign: 'right', padding: '8px 12px' }}>TOTAL</td>
                <td style={{ textAlign: 'right', color: 'var(--accent)' }}>{INR(totalBillAmt)}</td>
                <td></td>
                <td style={{ textAlign: 'right', color: 'var(--green)' }}>{INR(totalPaidAmt)}</td>
                <td colSpan={5}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* SPLIT BILL MODAL */}
      {splitBill && splitSavedRows && (() => {
        const rows = splitSavedRows;
        const savedAmt = Number(rows[0].bill_amount) || 0;
        const company = Number(rows[0].company_contribution) || 0;
        const totalShare = rows.reduce((t, r) => t + (Number(r.share_amount) || 0), 0);
        const nowAmt = Number(splitBill.bill_amount) || 0;
        const printSaved = () => {
          const w = window.open('', '_blank'); if (!w) return;
          const trs = rows.map((r, i) => '<tr><td>' + (i + 1) + '</td><td>' + (r.register_number || '') + '</td><td>' + (r.student_name || '') + '</td><td style="text-align:center">' + (r.days_present ?? '') + '</td><td style="text-align:right">₹' + (Number(r.share_amount) || 0) + '</td></tr>').join('');
          w.document.write('<html><head><title>Bill Split</title><style>@page{size:A4;margin:15mm}body{font-family:Arial,sans-serif;font-size:12px}h2{margin-bottom:4px}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #999;padding:5px 8px}th{background:#eee}</style></head><body><h2>' + splitBill.bill_type + ' Bill Split — ' + splitBill.hostel + ' Hostel</h2><div><b>Month:</b> ' + splitBill.month_year + ' &nbsp;|&nbsp; <b>Bill:</b> ₹' + savedAmt + ' &nbsp;|&nbsp; <b>Company:</b> ₹' + company + ' &nbsp;|&nbsp; <b>Students:</b> ₹' + totalShare + '</div><table><thead><tr><th>#</th><th>Reg No</th><th>Student</th><th>Days</th><th>Share</th></tr></thead><tbody>' + trs + '</tbody></table></body></html>');
          w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
        };
        return (
          <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSplitBill(null)}>
            <div className="modal modal-lg" style={{maxWidth:700}}>
              <div className="modal-header">
                <span className="modal-title">🔒 Saved Split — {splitBill.bill_type} {splitBill.hostel} ({splitBill.month_year})</span>
                <button className="btn btn-ghost btn-sm" onClick={()=>setSplitBill(null)}><X size={16}/></button>
              </div>
              <div className="modal-body">
                <div style={{background:'var(--bg3)',borderRadius:8,padding:'8px 12px',marginBottom:10,fontSize:12}}>
                  This split is already saved and added to the students’ hostel bills. These amounts are locked — vacating or deleting a student does not change them.
                </div>
                <div style={{display:'flex',gap:16,flexWrap:'wrap',fontSize:12,marginBottom:8}}>
                  <div><b>Bill at split:</b> ₹{savedAmt}</div>
                  <div><b>Company:</b> ₹{company}</div>
                  <div><b>Students:</b> {rows.length} · ₹{totalShare}</div>
                </div>
                {savedAmt > 0 && Math.abs(nowAmt - savedAmt) > 0.5 && (
                  <div style={{color:'var(--red)',fontWeight:700,fontSize:12,marginBottom:8}}>⚠ The bill is now ₹{nowAmt} but the split was made on ₹{savedAmt}.</div>
                )}
                <div style={{maxHeight:'50vh',overflowY:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{background:'#f3f4f6'}}>
                      <th style={{padding:'6px 8px',textAlign:'left'}}>Reg No</th>
                      <th style={{padding:'6px 8px',textAlign:'left'}}>Student</th>
                      <th style={{padding:'6px 8px',textAlign:'center'}}>Days</th>
                      <th style={{padding:'6px 8px',textAlign:'right'}}>Share (₹)</th>
                    </tr></thead>
                    <tbody>{rows.map(r => (
                      <tr key={r.student_id + '_' + r.register_number} style={{borderTop:'1px solid var(--border)'}}>
                        <td style={{padding:'5px 8px'}}>{r.register_number}</td>
                        <td style={{padding:'5px 8px'}}>{r.student_name}</td>
                        <td style={{padding:'5px 8px',textAlign:'center'}}>{r.days_present}</td>
                        <td style={{padding:'5px 8px',textAlign:'right',fontWeight:600}}>₹{Number(r.share_amount) || 0}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                <div style={{display:'flex',gap:8,justifyContent:'space-between',marginTop:12,flexWrap:'wrap'}}>
                  <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={() => {
                    if (!window.confirm('Recalculate this split?\n\nThis REPLACES the saved shares and changes the amounts already added to the students’ hostel bills. Do this only if the bill amount itself was wrong.')) return;
                    setSplitSavedRows(null);
                  }}>Recalculate…</button>
                  <div style={{display:'flex',gap:8}}>
                    <button className="btn btn-secondary" onClick={()=>setSplitBill(null)}>Close</button>
                    <button className="btn btn-primary" onClick={printSaved}>🖨 Print</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {splitBill && !splitSavedRows && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSplitBill(null)}>
          <div className="modal modal-lg" style={{maxWidth:700}}>
            <div className="modal-header">
              <span className="modal-title">Split {splitBill.bill_type} Bill — {splitBill.hostel} ({splitBill.month_year}) {splitBill.combined_count>1?`[${splitBill.combined_count} bills combined]`:''}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setSplitBill(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:8,padding:'10px 14px',marginBottom:12}}>
                <div style={{display:'flex',gap:24,flexWrap:'wrap'}}>
                  <div><b>Hostel:</b> {splitBill.hostel}</div>
                  <div><b>Period:</b> {fmtDMY(splitBill.prev_reading_date)} → {fmtDMY(splitBill.bill_date)}</div>
                  <div><b>Total Bill:</b> ₹{splitBill.bill_amount||0} {splitBill.combined_count>1?<span style={{fontSize:10,color:'var(--text3)'}}>({splitBill.combined_count} {splitBill.bill_type} connections combined)</span>:''}</div>
                </div>
              </div>
              <div className="form-group" style={{marginBottom:12}}>
                <label><b>Company Contribution (₹)</b> — Amount Armstech pays to reduce student burden</label>
                <input type="number" value={companyContrib} onChange={e=>setCompanyContrib(Number(e.target.value)||0)} style={{maxWidth:200}} placeholder="0"/>
              </div>
              {(() => {
                const billStart = new Date(splitBill.prev_reading_date);
                const billEnd = new Date(splitBill.bill_date);
                const totalDays = Math.max(1, Math.round((billEnd-billStart)/(1000*60*60*24)));
                const amtToSplit = Math.max(0, (splitBill.bill_amount||0) - companyContrib);
                const activeRaw = splitStudents.filter(a => {
                  const joined = new Date(a.joined_date);
                  const vacated = a.vacate_date ? new Date(a.vacate_date) : null;
                  return joined <= billEnd && (!vacated || vacated >= billStart);
                }).map(a => {
                  const joined = new Date(a.joined_date);
                  const vacated = a.vacate_date ? new Date(a.vacate_date) : null;
                  const start = joined > billStart ? joined : billStart;
                  const end = vacated && vacated < billEnd ? vacated : billEnd;
                  const days = Math.max(0, Math.round((end-start)/(1000*60*60*24)));
                  return {...a, days};
                });
                const totalStudentDays = activeRaw.reduce((s,a)=>s+a.days, 0);
                const active = activeRaw.map(a => ({
                  ...a,
                  share: totalStudentDays>0 ? Math.round((a.days/totalStudentDays)*amtToSplit) : 0
                }));
                return (
                  <div>
                    <div style={{marginBottom:8,fontWeight:600}}>Students ({active.length}) — Amount to split: ₹{amtToSplit} | Bill period: {totalDays} days | Total student-days: {totalStudentDays}</div>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead><tr style={{background:'#f3f4f6'}}>
                        <th style={{padding:'6px 8px',textAlign:'left'}}>Student</th>
                        <th style={{padding:'6px 8px',textAlign:'left'}}>Reg No</th>
                        <th style={{padding:'6px 8px',textAlign:'center'}}>Days</th>
                        <th style={{padding:'6px 8px',textAlign:'right'}}>Share (₹)</th>
                      </tr></thead>
                      <tbody>{active.map((a,i)=>(
                        <tr key={a.id} style={{background:i%2?'#f9fafb':'#fff'}}>
                          <td style={{padding:'5px 8px'}}>{a.students?.full_name||'—'}</td>
                          <td style={{padding:'5px 8px',color:'var(--text3)'}}>{a.students?.register_number||'—'}</td>
                          <td style={{padding:'5px 8px',textAlign:'center'}}>{a.days} days</td>
                          <td style={{padding:'5px 8px',textAlign:'right',fontWeight:600}}>₹{a.share}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                    <div style={{marginTop:8,padding:'6px 8px',background:'#fef3c7',borderRadius:4,fontSize:12}}>
                      <b>Company:</b> ₹{companyContrib} | <b>Students Total:</b> ₹{active.reduce((s,a)=>s+a.share,0)} | <b>Bill Total:</b> ₹{splitBill.bill_amount||0}
                    </div>
                    <div className="modal-footer" style={{marginTop:12,padding:0}}>
                      <button className="btn btn-secondary" onClick={()=>setSplitBill(null)}>Cancel</button>
                      <button className="btn btn-secondary" onClick={()=>{
                        const w=window.open('','_blank');
                        const rows=active.map((a,i)=>`<tr><td>${i+1}</td><td>${a.students?.full_name||'—'}</td><td>${a.students?.register_number||'—'}</td><td style="text-align:center">${a.days}</td><td style="text-align:right">₹${a.share}</td></tr>`).join('');
                        w.document.write(`<html><head><title>Bill Split</title><style>@page{size:A4;margin:15mm}body{font-family:Arial,sans-serif;font-size:12px}h2{margin-bottom:4px}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #ddd;padding:6px 10px}th{background:#f3f4f6;font-weight:700}.info{background:#f0f9ff;padding:8px 12px;border-radius:4px;margin-bottom:10px;font-size:11px}.summary{background:#fef3c7;padding:6px 12px;margin-top:10px;font-size:11px}</style></head><body>
                        <h2>${splitBill.bill_type} Bill Split — ${splitBill.hostel} Hostel</h2>
                        <div class="info">
                          <b>Period:</b> ${splitBill.prev_reading_date} to ${splitBill.bill_date} (${totalDays} days) &nbsp;|&nbsp;
                          <b>Month:</b> ${splitBill.month_year} &nbsp;|&nbsp;
                          <b>Total Bill:</b> ₹${splitBill.bill_amount} ${splitBill.combined_count>1?'('+splitBill.combined_count+' connections combined)':''}<br/>
                          <b>Company Contribution:</b> ₹${companyContrib} &nbsp;|&nbsp;
                          <b>Amount to Split:</b> ₹${amtToSplit} &nbsp;|&nbsp;
                          <b>Total Student-Days:</b> ${totalStudentDays}
                        </div>
                        <table><thead><tr><th>#</th><th>Student Name</th><th>Register No</th><th>Days Present</th><th>Share Amount</th></tr></thead><tbody>${rows}</tbody></table>
                        <div class="summary"><b>Total:</b> Company ₹${companyContrib} + Students ₹${active.reduce((s,a)=>s+a.share,0)} = ₹${splitBill.bill_amount}</div>
                        </body></html>`);
                        w.document.close();setTimeout(()=>w.print(),300);
                      }}>🖨️ Print</button>
                      <button className="btn btn-primary" disabled={splitSaving||active.length===0} onClick={async()=>{
                        setSplitSaving(true);
                        // Delete existing splits for this bill
                        await supabase.from('hostel_bill_splits').delete().in('bill_id', [splitBill.id, splitBill.saved_src].filter(Boolean));
                        // Insert new splits
                        const rows = active.map(a=>({
                          bill_id: splitBill.id,
                          combined_bill_ids: splitBill.combined_bill_ids||[splitBill.id],
                          student_id: a.student_id,
                          student_name: a.students?.full_name||'',
                          register_number: a.students?.register_number||'',
                          hostel: splitBill.hostel,
                          join_date: a.joined_date,
                          vacate_date: a.vacate_date||null,
                          days_present: a.days,
                          total_days: totalDays,
                          bill_amount: splitBill.bill_amount||0,
                          company_contribution: companyContrib,
                          share_amount: a.share,
                          paid_status: 'No',
                        }));
                        const {error} = await supabase.from('hostel_bill_splits').insert(rows);
                        if(error) { toast.error(error.message); setSplitSaving(false); return; }
                        // Auto-update hostel_utility_bills for each student
                        const [monthStr, yearStr] = (splitBill.month_year||'').split(' ');
                        const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                        const billingMonth = MONTHS.indexOf(monthStr)+1;
                        const billingYear = Number(yearStr);
                        let utilErr = null;
                        if (billingMonth>0 && billingYear>0) {
                          const utilRows = active.map(a => {
                            const isKSEB = splitBill.bill_type==='KSEB';
                            return {
                              student_id: a.student_id,
                              hostel_allocation_id: a.id,
                              billing_month: billingMonth,
                              billing_year: billingYear,
                              electricity_amount: isKSEB ? a.share : 0,
                              water_amount: isKSEB ? 0 : a.share,
                            };
                          });
                          const byStudent = {};
                          utilRows.forEach(r => {
                            if (!byStudent[r.student_id]) byStudent[r.student_id] = { ...r };
                            else { byStudent[r.student_id].electricity_amount += r.electricity_amount; byStudent[r.student_id].water_amount += r.water_amount; }
                          });
                          // Upsert each student once - merge electricity/water amounts
                          for (const row of Object.values(byStudent)) {
                            const {data:existing} = await supabase.from('hostel_utility_bills')
                              .select('id,electricity_amount,water_amount')
                              .eq('student_id', row.student_id)
                              .eq('billing_month', row.billing_month)
                              .eq('billing_year', row.billing_year)
                              .single();
                            const elec = existing ? Number(existing.electricity_amount||0) : 0;
                            const water = existing ? Number(existing.water_amount||0) : 0;
                            const newElec = row.electricity_amount > 0 ? row.electricity_amount : elec;
                            const newWater = row.water_amount > 0 ? row.water_amount : water;
                            const { error: upErr } = await supabase.from('hostel_utility_bills').upsert({
                              ...row,
                              electricity_amount: newElec,
                              water_amount: newWater,
                            }, {onConflict:'student_id,billing_month,billing_year'});
                            if (upErr && !utilErr) utilErr = upErr.message;
                          }
                        }
                        if (utilErr) toast.error('Split saved, but the students’ hostel bills were NOT updated: ' + utilErr, { duration: 15000 });
                        else toast.success(`Split saved — ${active.length} students, utility bills updated`);
                        setSplitBill(null);
                        setSplitSaving(false);
                        loadSplits();
                      }}>{splitSaving?'Saving...':'Save Split ('+active.length+' students)'}</button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal modal-lg" style={{ maxWidth: 720, maxHeight: '92vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit' : 'Add'} Bill Entry</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">

              {/* A — Connection Details */}
              <div className="form-section-heading">A — Connection Details</div>
              <div className="form-grid">
                <div className="form-group full">
                  <label>Connection Name *</label>
                  <select value={form.connection_name} onChange={e => onConnectionSelect(e.target.value)}>
                    <option value="">Select Connection</option>
                    {HOSTELS.map(h => (
                      <optgroup key={h} label={`── ${h} Hostel ──`}>
                        {HOSTEL_CONNECTIONS.filter(c => c.hostel === h).map(c => (
                          <option key={c.name} value={c.name}>
                            {c.type === 'KSEB' ? '⚡' : '💧'} {c.name}  {c.cons_no ? `(${c.cons_no})` : ''}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Hostel</label>
                  <select value={form.hostel} onChange={e => sf('hostel', e.target.value)}>
                    <option value="">Select</option>
                    {HOSTELS.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Bill Type</label>
                  <select value={form.bill_type} onChange={e => sf('bill_type', e.target.value)}>
                    <option value="KSEB">⚡ KSEB</option>
                    <option value="KWA">💧 KWA</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Consumer Number</label>
                  <input value={form.cons_no} onChange={e => sf('cons_no', e.target.value)} placeholder="Consumer/connection number" />
                </div>
                <div className="form-group">
                  <label>Month & Year *</label>
                  <input value={form.month_year} onChange={e => sf('month_year', e.target.value)} placeholder="e.g. Apr 2026" />
                </div>
              </div>

              {/* B — Meter & Bill Details */}
              <div className="form-section-heading" style={{ marginTop: 14 }}>B — Meter & Bill Details</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Previous Meter Reading</label>
                  <input type="number" value={form.prev_meter_reading||''} onChange={e => sf('prev_meter_reading', e.target.value)} placeholder="Previous reading"/>
                </div>
                <div className="form-group">
                  <label>Present Meter Reading</label>
                  <input type="number" value={form.meter_reading} onChange={e => sf('meter_reading', e.target.value)} placeholder="Current reading" />
                </div>
                <div className="form-group">
                  <label>Consumption (units)</label>
                  <input type="number" value={form.consumption} onChange={e => sf('consumption', e.target.value)} placeholder="Units consumed" />
                </div>
                <div className="form-group">
                  <label>Previous Reading Date</label>
                  <DateInput value={form.prev_reading_date||''} onChange={e => sf('prev_reading_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Present Reading Date</label>
                  <DateInput value={form.bill_date} onChange={e => sf('bill_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Bill Due Date</label>
                  <DateInput value={form.bill_due_date} onChange={e => sf('bill_due_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Bill Disconnection Date</label>
                  <DateInput value={form.bill_disconnection_date} onChange={e => sf('bill_disconnection_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Bill Amount (₹)</label>
                  <input type="number" value={form.bill_amount} onChange={e => sf('bill_amount', e.target.value)} placeholder="0.00" />
                </div>
              </div>

              {/* C — Payment Details */}
              <div className="form-section-heading" style={{ marginTop: 14 }}>C — Payment Details</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Paid or Not</label>
                  <select value={form.paid_status} onChange={e => sf('paid_status', e.target.value)}>
                    {PAID_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Paid Amount (₹)</label>
                  <input type="number" value={form.paid_amount} onChange={e => sf('paid_amount', e.target.value)} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label>Paid Date</label>
                  <DateInput value={form.paid_date} onChange={e => sf('paid_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Contribution</label>
                  <select value={form.contribution} onChange={e => sf('contribution', e.target.value)}>
                    <option value="">Select</option>
                    {CONTRIBUTION.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Payment Type</label>
                  <select value={form.payment_type} onChange={e => sf('payment_type', e.target.value)}>
                    {['Cash','Bank Transfer','Cheque','UPI','Online'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Payment Mode <span style={{fontSize:10,color:'var(--accent)'}}>→ syncs to Accounts</span></label>
                  <select value={form.payment_mode} onChange={e => sf('payment_mode', e.target.value)}>
                    <option value="">Select Account</option>
                    {['EKM FDO','EKM Admin','TVM FDO','TVM Admin','Cash','HDFC Vazhakkala','HDFC Kottarakkara','SBI','Ujjivan'].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select value={form.category} onChange={e => sf('category', e.target.value)}>
                    {['Utilities','Office Expenses','Miscellaneous','Other Income','Rent'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Receipt / Reference No</label>
                  <input value={form.receipt_no} onChange={e => sf('receipt_no', e.target.value)} placeholder="Receipt or transaction ref" />
                </div>
                <div className="form-group full">
                  <label>Remarks</label>
                  <textarea rows={2} value={form.remarks} onChange={e => sf('remarks', e.target.value)} placeholder="Any additional notes..." />
                </div>
              </div>
              {form.paid_status === 'Yes' && form.paid_date && (
                <div style={{marginTop:10,padding:'8px 12px',background:'#f0fdf4',border:'1px solid #86efac',borderRadius:8,fontSize:11,color:'#15803d'}}>
                  💡 This entry will auto-sync to <strong>Accounts → ARMSTECH → Cash Out</strong> under <strong>{form.payment_mode || 'selected Payment Mode'}</strong> when saved.
                </div>
              )}

            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
